import { Injectable } from '@angular/core';
import { MyDevice } from 'src/app/data-models/device/my-device.model';
import WinRT from '@lenovo/tan-client-bridge-le/src/util/winrt';
import { CommonService } from '../common/common.service';
import { Microphone } from 'src/app/data-models/audio/microphone.model';
import { DeviceMonitorStatus } from 'src/app/enums/device-monitor-status.enum';
import { Router } from '@angular/router';
import { HypothesisService } from '../hypothesis/hypothesis.service';
import { LoggerService } from '../logger/logger.service';
import { VantageShellService } from '../vantage-shell/vantage-shell.service';
import { resolve } from 'url';
import { FeatureToggleService } from '../feature-toggle/feature-toggle.service';

@Injectable({
	providedIn: 'root',
})
export class DeviceService {
	private device: any;
	private sysInfo: any;
	private microphone: any;
	public isShellAvailable = false;
	public is64bit = true;
	public isLiteGaming = false;
	public isMMD: boolean | undefined = undefined; // machine ship with Microsoft Managed Desktop
	public isWarrantyAvailable = false;
	public machineInfo: any;
	public showSearch = false;
	public machineType: number;
	private Windows: any;

	get showWarranty() {
		return this.isWarrantyAvailable && this.featureToggleService.isFeatureAvailable('warranty');
	}

	constructor(
		private shellService: VantageShellService,
		private commonService: CommonService,
		private router: Router,
		private logger: LoggerService,
		private hypSettings: HypothesisService,
		private featureToggleService: FeatureToggleService
	) {
		this.device = shellService.getDevice();
		this.sysInfo = shellService.getSysinfo();
		this.microphone = shellService.getMicrophoneSettings();
		this.Windows = this.shellService.getWindows();
		if (this.device && this.sysInfo) {
			this.isShellAvailable = true;
		}
		this.initShowSearch();
	}

	private initShowSearch() {
		if (this.hypSettings) {
			this.hypSettings.getFeatureSetting('LE_FeatureSearch').then(
				(searchFeature) => {
					this.showSearch = (searchFeature || '').toString() === 'true';
				},
				(error) => {
					this.logger.error('DeviceService.initShowSearch: promise rejected ', error);
				}
			);
		}
	}

	public getDeviceInfo(): Promise<MyDevice> {
		if (this.device) {
			return this.device.getDeviceInfo();
		}
		return undefined;
	}

	// this API doesn't have performance issue, can be always called at any time.
	getMachineInfo(): Promise<any> {
		this.logger.debug('DeviceService.getMachineInfo: pre API call');
		if (this.machineInfo) {
			this.logger.debug('DeviceService.getMachineInfo: found cached response');
			this.commonService.sendNotification('MachineInfo', this.machineInfo);
			return Promise.resolve(this.machineInfo);
		}

		if (this.isShellAvailable && this.sysInfo) {
			this.logger.debug('DeviceService.getMachineInfo: no cache, invoking API');

			return this.sysInfo.getMachineInfo().then((info) => {
				this.logger.debug('DeviceService.getMachineInfo: response received from API');
				this.machineInfo = info;
				if (
					!this.isWarrantyAvailable &&
					(!info.mtm || (info.mtm && !info.mtm.toLocaleLowerCase().endsWith('cd')))
				) {
					this.isWarrantyAvailable = true;
				}
				if (info && info.cpuArchitecture) {
					if (info.cpuArchitecture.indexOf('64') === -1) {
						this.is64bit = false;
					} else {
						this.is64bit = true;
					}
				}
				this.commonService.sendNotification('MachineInfo', this.machineInfo);
				this.logger.debug('DeviceService.getMachineInfo: returning response from API');
				return info;
			});
		}
		return Promise.resolve(undefined);
	}

	getMMDInfo(): Promise<any> {
		if (this.isMMD) {
			return Promise.resolve(this.isMMD);
		} else {
			const regUtil = this.shellService.getRegistryUtil();
			if (regUtil) {
				return this.shellService
					.calcDeviceFilter({ var: 'DeviceTags.System.Profile.MMD' })
					.then((val) => {
						this.isMMD = val != null;
						return this.isMMD;
					});
			} else {
				return Promise.resolve(undefined);
			}
		}
	}

	getMachineInfoSync(): any {
		return this.machineInfo;
	}

	getHardwareInfo(): Promise<any> {
		if (this.sysInfo) {
			return this.sysInfo.getHardwareInfo();
		}
		return undefined;
	}

	getAllDisksUsage(): Promise<any> {
		if (this.sysInfo) {
			return this.sysInfo.getAllDisksUsage();
		}
		return undefined;
	}

	getMemAndDiskUsage(): Promise<any> {
		if (this.sysInfo) {
			return this.sysInfo.getMemAndDiskUsage();
		}
		return undefined;
	}

	public launchUri(path: string) {
		if (WinRT.launchUri && path) {
			WinRT.launchUri(path);
		}
	}

	public startMicrophoneMonitor() {
		if (this.microphone) {
			this.microphone.startMonitor((response: Microphone) => {
				this.commonService.sendNotification(DeviceMonitorStatus.MicrophoneStatus, response);
			});
		}
	}

	public stopMicrophoneMonitor() {
		if (this.microphone) {
		}
	}

	getMachineType(): Promise<number> {
		if (this.sysInfo) {
			if (this.machineType) {
				return Promise.resolve(this.machineType);
			}
			return this.sysInfo.getMachineType((value) => {
				this.machineType = value;
				return value;
			});
		}
		return undefined;
	}
	/**
	 * get is ARM
	 * @returns Promise<boolean>
	 */
	IsArm(): Promise<boolean> {
		if (this.machineInfo) {
			this.logger.info('DeviceService.IsArm: found cached response');
			return Promise.resolve(
				this.machineInfo &&
					this.machineInfo.cpuArchitecture &&
					this.machineInfo.cpuArchitecture.toUpperCase().trim() === 'ARM64'
			);
		}
		if (this.isShellAvailable) {
			return this.getMachineInfo()
				.then((res) => {
					return (
						res &&
						res.cpuArchitecture &&
						res.cpuArchitecture.toUpperCase().trim() === 'ARM64'
					);
				})
				.catch((error) => {
					this.logger.error('DeviceService.IsARM' + error.message);
					return false;
				});
		}
		return undefined;
	}
}
